window.StockCubeCore = window.StockCubeCore || {};

window.StockCubeCore.calculateBehavioralFlags = function () {
    const dataSource = this.data.dataSource;

    const gradientSources   = ['top-dollar-vol-sec', 'watchlist-reports'];
    const fixedSources      = ['top-gainers', 'top-losers', 'top-dollar-vol-sec', 'top-dollar-vol',
                               'top-gainers-sec', 'top-losers-sec', 'top-vol-sec', 'top-vol'];
    const hasSectors        = ['top-vol-sec', 'top-dollar-vol-sec', 'top-gainers-sec', 'top-losers-sec',
                               'sip-sec', 'dividend-sec'];
    const mmapModeSources = ['mmap'];
    const sectorColorSources = ['top-gainers-sec', 'top-losers-sec', 'top-vol-sec', 'sip-sec', 'dividend-sec'];

    const isMmap3D = mmapModeSources.includes(dataSource) && this.mmapViewMode === '3d';

    const isGradient = gradientSources.includes(dataSource) || isMmap3D;
    this.colorMode    = (isGradient && !sectorColorSources.includes(dataSource)) ? 'gradient' : 'sector';
    this.isFixed      = fixedSources.includes(dataSource);
    this.isHasSectors = hasSectors.includes(dataSource) || isMmap3D;
    this.isMmapMode   = mmapModeSources.includes(dataSource) && !isMmap3D;
};

window.StockCubeCore.init = async function () {
    this.scene = document.querySelector('a-scene');
    const geometry = this.el.getAttribute('geometry') || { height: 2 };
    this.containerHeight = geometry.height || 2;
    this.maxHeight = 0.4;
    this.spacing = 0.03 * this.containerHeight / 2;
    this.fontSize = this.platformConfig.fontSize;
    this.baseOffset = this.containerHeight * 0.02;
    this.zeroPlaneOffset = -0.2;
    this.labelYOffset = this.platformConfig.labelYOffset;
    this.usableHeight = this.platformConfig.usableHeight;
    this.isChartDataReady = false;
    this.waitingForChartData = false;

    await this.beforeInit?.();

    this.onInjectedData = (evt) => {
        console.log('[StockCubeVisualizer] Received injected data via event');
        if (evt.detail) {
            this.injectedDataCache = evt.detail;
            if (this.data.dataSource === 'watchlist-reports') {
                console.log('[StockCubeVisualizer] Force re-render for injected data');
                this.isFirstRender = true;
                this.fetchStockData();
            }
        }
    };
    this.el.addEventListener('watchlist-injected-data', this.onInjectedData);

    if (window.StockCubeCore && window.StockCubeCore.cancelTimelineAnimation) {
        window.StockCubeCore.cancelTimelineAnimation(this.data.timelineEntity);
    }

    this.polygonApiManager = null;
    this.pvtechApiManager = null;
    this.companyIconApiManager = null;
    this.firstUpdate = true;
    this.stockBars = this.stockBars || new Map();
    this.sectorLabels = this.sectorLabels || new Map();

    const baseOffsetFraction = this.baseOffset / this.containerHeight;
    if (this.maxHeight + baseOffsetFraction > 1.0) {
        console.warn(`maxHeight (${this.maxHeight}) + baseOffset/containerHeight (${baseOffsetFraction}) exceeds 1.0.`);
    }

    this.currentTimestamp = null;
    this.currentFetchId = 0;
    this.currentData = null;
    this.isInitialized = false;
    this.rotationData = null;
    this.isFirstAnimationPlay = true;
    this.hasShownZeroFrame = false;

    this.sectorNames = {
        1: "Utilities",
        2: "Energy",
        3: "Materials",
        4: "Finance",
        5: "Health",
        6: "Real Estate",
        7: "Technology",
        8: "Industrials",
        9: "Communications",
        10: "Cons. Staples",
        11: "Cons. Discretionary"
    };

    this.calculateBehavioralFlags();

    this._maxMmapCacheEntries = 20;
    this.mmapChangePercExtents = null;

    this.sectorPlatforms = new Map();

    this.emissivePulse = {
        epochMs: 0,
        periodMs: 3000,
        bars: new Map()
    };

    if (this.data.timelineEntity) {
        this.data.timelineEntity.addEventListener('goto-time', (event) => {
            const { timestamp, index } = event.detail;
            console.log(`Received goto-time: timestamp=${timestamp}, index=${index}`);
            this.gotoTimestamp(timestamp, index);
        });
    }

    if (this.data.interval) {
        this.scene.addEventListener('data-interval', (event) => {
            if (document.body.dataset.isSearchMode === 'true') {
                console.log('Ignoring data-interval in stock-cube-visualizer because Search Mode is active');
                return;
            }
            const { interval } = event.detail;
            console.log(`Received data-interval: interval=${interval}`);
            this.el.setAttribute('stock-cube-visualizer', 'interval', interval.toLowerCase());
            this.emitContextEvent();
        });
    }

    this.setupChartDataListeners();
    this.setupRotationListener();

    if (!this.sipFilters || typeof this.sipFilters.minPrice === 'undefined' || typeof this.sipFilters.minCap === 'undefined') {
        const sipFiltersEl = document.querySelector('[sip-filters]');
        const sipFiltersData = sipFiltersEl && sipFiltersEl.getAttribute
            ? (sipFiltersEl.getAttribute('sip-filters') || {})
            : {};
        const initialMinPrice = Number(sipFiltersData.minPrice ?? this.sipFilters?.minPrice ?? 1);
        const initialMinCap = Number(sipFiltersData.minCap ?? this.sipFilters?.minCap ?? 10_000_000_000);
        this.sipFilters = { minPrice: initialMinPrice, minCap: initialMinCap };
    }

    this.sipFiltersChangedListener = (event) => {
        const isSip = ['sip', 'sip-sec', 'dividend', 'dividend-sec'].includes(this.data.dataSource);
        if (!isSip) return;
        try {
            const incoming = event && event.detail ? event.detail : {};
            const minPrice = Number(incoming.minPrice ?? this.sipFilters?.minPrice ?? 1);
            const minCap = Number(incoming.minCap ?? this.sipFilters?.minCap ?? 10_000_000_000);
            this.sipFilters = { minPrice, minCap };
            this._cachedGlobalMax = null;
            this.maxHeightValue = undefined;
            this.hasNegativeGlobal = false;
            this.isFirstRender = true;
            this.renderFinalFrame(this.currentData || { intervals: {} });
            setTimeout(() => {
                if (this.scene) {
                    this.scene.emit('update-rotation', this.rotationData || { x: 0, y: 0, z: 0 });
                    this.scene.emit('update-billboards');
                }
                this.calculateSectorLabelPosition?.(this.rotationData);
            }, 250);
        } catch (e) {
            console.warn('Failed to re-render on sip-filters change', e);
        }
    };
    if (this.scene) {
        this.scene.addEventListener('sip-filters-changed', this.sipFiltersChangedListener);
    }

    const defaultConfig = {
        isAnimationOn: true,
        isGlowOn: false,
        glowThreshold: '+200%',
        isFireworkOn: false,
        fireworkThreshold: '+10%',
        chartMathFunction: 'Linear'
    };
    this.configSettings = { ...defaultConfig };
    try {
        const storedConfig = getCacheItem('configureControlSettings');
        if (storedConfig && typeof storedConfig === 'object') {
            const configData = storedConfig.value && typeof storedConfig.value === 'object'
                ? storedConfig.value
                : storedConfig;
            this.configSettings = Object.assign({}, this.configSettings, configData);
        } else {
            const rawConfig = localStorage.getItem('configureControlSettings');
            if (rawConfig) {
                const parsed = JSON.parse(rawConfig);
                const configData = parsed.value && typeof parsed.value === 'object' ? parsed.value : parsed;
                this.configSettings = Object.assign({}, this.configSettings, configData);
            }
        }
    } catch (e) {
        console.warn('Failed to parse configureControlSettings from storage', e);
    }
    this.fireworksController?.updateThreshold();

    this.configureChangedListener = (event) => {
        const settings = event.detail?.settings || {};
        console.log('[StockCubeVisualizer] Configuration changed:', settings);
        const isVRPlatform = this.platformConfig && this.platformConfig.platform === 'vr';

        const oldAnimationOn = this.getConfigSetting('isAnimationOn', true);
        Object.assign(this.configSettings, settings);

        if (settings.isAnimationOn === true && !oldAnimationOn) {
            console.log('[StockCubeVisualizer] Animation toggled ON, re-triggering animation');
            this.isFirstAnimationPlay = true;
            this.hasShownZeroFrame = false;
            if (window.StockCubeCore && window.StockCubeCore.cancelTimelineAnimation) {
                window.StockCubeCore.cancelTimelineAnimation(this.data.timelineEntity);
            }
            if (this.currentData) {
                if (this.isMmapMode && this.data.dataSource === 'mmap') {
                    window.MarketMapSharedRendering.renderMarketMap(this, this.currentData);
                } else {
                    this.animateStockBars(this.currentData, this.currentFetchId);
                }
            }
        } else if (settings.isAnimationOn === false && oldAnimationOn) {
            if (window.StockCubeCore && window.StockCubeCore.cancelTimelineAnimation) {
                window.StockCubeCore.cancelTimelineAnimation(this.data.timelineEntity);
            }
            if (this.currentData) {
                if (this.isMmapMode && this.data.dataSource === 'mmap') {
                    window.MarketMapSharedRendering.renderMarketMap(this, this.currentData);
                } else {
                    this.renderFinalFrame(this.currentData);
                }
            }
        }

        if (settings.isGlowOn !== undefined || settings.glowThreshold !== undefined) {
            if (this.scene && settings.isGlowOn !== undefined) {
                if (isVRPlatform) {
                    // this.scene.setAttribute('bloom', { enabled: true });
                } else {
                    this.scene.setAttribute('bloom', { enabled: !!this.getConfigSetting('isGlowOn', false) && this.getQualityLevel() === 'quality' });
                }
            }
            this.refreshEmissiveForAllBars();
        }

        if (settings.isFireworkOn !== undefined || settings.fireworkThreshold !== undefined) {
            this.fireworksController?.updateThreshold();
            if (settings.isFireworkOn === false) {
                this.fireworksController?.clearFireworkIntervals();
            }
        }

        if (settings.chartMathFunction !== undefined) {
            this._cachedGlobalMax = null;
            this.maxHeightValue = undefined;
            this.isFirstRender = true;
            this.renderFinalFrame(this.currentData);
        }
    };
    
    this.getQualityLevel = () => {
        const mainSettings = getCacheItem('mainSettings');
        let quality = 'quality';
        if (mainSettings) {
            const isMobile = /(iPhone|iPad|iPod|Android|Mobi|Tablet)/i.test(navigator.userAgent);
            quality = mainSettings.quality ? mainSettings.quality : (isMobile ? 'performance' : 'quality');
        }
        return quality;
    }
    
    document.addEventListener('configure-changed', this.configureChangedListener);
    this.el.addEventListener('configure-changed', this.configureChangedListener);

    try {
        this.stockInfo = [];
        this.currentFetchId = 0;
        this.isFirstRender = true;
        this.isFirstAnimationPlay = true;
        this.hasShownZeroFrame = false;

        this.errorDialog = document.createElement('a-entity');
        this.errorDialog.setAttribute('geometry', { primitive: 'plane', width: 2, height: 1 });
        this.errorDialog.setAttribute('material', { color: '#333', opacity: 0.9 });
        this.errorDialog.setAttribute('position', '0 1.5 -2');
        this.errorDialog.setAttribute('visible', false);
        const errorText = document.createElement('a-text');
        errorText.setAttribute('value', '');
        errorText.setAttribute('color', 'white');
        errorText.setAttribute('align', 'center');
        errorText.setAttribute('width', 1.8);
        errorText.setAttribute('wrap-count', 20);
        errorText.setAttribute('position', '0 0 0.01');
        this.errorDialog.appendChild(errorText);
        this.el.sceneEl.appendChild(this.errorDialog);
        this.errorText = errorText;

        console.log('🔧 Initializing API managers...');

        try {
            console.log('✅ PolygonAPIManager initialization started');
            this.polygonApiManager = await PolygonAPIManager.create();
            console.log('✅ PolygonAPIManager initialized successfully');
        } catch (error) {
            console.error('❌ Failed to initialize PolygonAPIManager:', error);
            this.showError('Failed to initialize Polygon API Manager');
            return;
        }

        try {
            console.log('✅ PVTechAPIManager initialization started');
            this.pvtechApiManager = await PVTechAPIManager.create();
            console.log('✅ PVTechAPIManager initialized successfully');
        } catch (error) {
            console.error('❌ Failed to initialize PVTechAPIManager:', error);
            this.pvtechApiManager = null;
        }

        try {
            if (typeof CompanyIconAPIManager !== 'undefined') {
                this.companyIconApiManager = await CompanyIconAPIManager.create();
                console.log('✅ CompanyIconAPIManager initialized');
            } else {
                console.warn('⚠️ CompanyIconAPIManager not available');
            }
        } catch (error) {
            console.error('❌ Failed to initialize CompanyIconAPIManager:', error);
        }

        this.watchlistApiManager = window.WatchlistAPIManager && window.WatchlistAPIManager.instance;

        if (!this.fireworksController) {
            const FWCtrl = window.FireworksController || (typeof FireworksController !== 'undefined' ? FireworksController : null);
            if (FWCtrl) {
                const isVRPlatform = this.platformConfig && this.platformConfig.platform === 'vr';
                const configureTarget = isVRPlatform ? this.el : document;
                this.fireworksController = new FWCtrl(this, configureTarget);
                this.fireworksController.init();
            }
        }

        if (this.data.selector && this.data.selector.components && this.data.selector.components['sector-selector']) {
            const selectorComp = this.data.selector.components['sector-selector'];
            const currentIds = this.data.sectorIds.map(Number).sort((a, b) => a - b);
            const defaultIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
            const isDefault = JSON.stringify(currentIds) === JSON.stringify(defaultIds) && this.data.topN === 6;
            if (isDefault) {
                this.data.sectorIds = Array.from(selectorComp.selectedSectors).sort((a, b) => a - b);
                this.data.topN = selectorComp.topN;
            }
        }

        this.isInitialized = true;

        try { this.emitContextEvent(); } catch (e) {
            console.warn('Failed to emit initial stock-cube-context', e);
        }

        try {
            if (this.scene) {
                const isVRPlatform = this.platformConfig && this.platformConfig.platform === 'vr';
                if (!isVRPlatform){
                    this.scene.setAttribute('bloom', { enabled: !!this.getConfigSetting('isGlowOn', false) && this.getQualityLevel() === 'quality' });
                }
                // this.scene.setAttribute('bloom', { enabled: isVRPlatform ? true : !!this.getConfigSetting('isGlowOn', false) });
            }
        } catch (e) {
            console.warn('Failed to apply bloom attribute from settings', e);
        }

        this.updateBanner();
        this.updateSelectorVisibility();

        const needsPVTech = ['top-gainers', 'top-losers', 'top-gainers-sec', 'top-losers-sec', 'top-vol-sec', 'top-vol',
            'top-dollar-vol', 'top-dollar-vol-sec'].includes(this.data.dataSource);

        if (needsPVTech && !this.pvtechApiManager) {
            this.showError('This data source requires PVTech API Manager which failed to initialize');
            return;
        }

        console.log('🚀 Starting initial data fetch...');
        await this.fetchStockData();

        console.log('✅ Stock cube visualizer initialization complete');

        this.afterInit?.();

    } catch (error) {
        console.error('❌ Failed to initialize stock-cube-visualizer:', error);
        this.showError(`Initialization error: ${error.message}`);
    }
};

window.StockCubeCore.update = function (oldData) {
    this.updateZeroPlane();
    const prevData = oldData || {};

    try {
        const intervalChanged = oldData && prevData.interval !== this.data.interval;
        const dataSourceChanged = oldData && prevData.dataSource !== this.data.dataSource;
        if (!oldData || intervalChanged || dataSourceChanged) {
            this.emitContextEvent();
        }
    } catch (e) {
        console.warn('Failed to evaluate/emit stock-cube-context in update', e);
    }

    const isDesktopPlatform = this.platformConfig && this.platformConfig.platform === 'desktop';
    if (isDesktopPlatform) {
        if (this.data.dataSource === 'watchlist-reports') {
            try {
                const wrapper = document.querySelector('#stock-3d-cube-wrapper');
                if (wrapper) {
                    wrapper.setAttribute('position', '0 0 .5');
                    if (wrapper.object3D) wrapper.object3D.position.set(0, 0, 0.5);
                }
            } catch (e) {
                console.warn('Failed to set wrapper position for watchlist-reports', e);
            }
        }
        if (this.data.dataSource === 'top-dollar-vol' || this.data.dataSource === 'top-dollar-vol-sec') {
            try {
                const wrapper = document.querySelector('#stock-3d-cube-wrapper');
                if (wrapper) {
                    wrapper.setAttribute('position', '0 0.250 .5');
                    if (wrapper.object3D) wrapper.object3D.position.set(0, 0.250, 0.5);
                }
            } catch (e) {
                console.warn('Failed to set wrapper position for top-dollar-vol', e);
            }
        }
        if (this.data.dataSource === 'top-gainers' || this.data.dataSource === 'top-gainers-sec') {
            try {
                const wrapper = document.querySelector('#stock-3d-cube-wrapper');
                if (wrapper) {
                    wrapper.setAttribute('position', '0 0 .5');
                    if (wrapper.object3D) wrapper.object3D.position.set(0, 0, 0.5);
                }
            } catch (e) {
                console.warn('Failed to set wrapper position for top-gainers', e);
            }
        }
        if (this.data.dataSource === 'top-losers' || this.data.dataSource === 'top-losers-sec') {
            try {
                const wrapper = document.querySelector('#stock-3d-cube-wrapper');
                if (wrapper) {
                    wrapper.setAttribute('position', '0 0.3 .5');
                    if (wrapper.object3D) wrapper.object3D.position.set(0, 0.3, 0.5);
                }
            } catch (e) {
                console.warn('Failed to set wrapper position for top-losers', e);
            }
        }
    }

    this.calculateBehavioralFlags();

    if (!this.pvtechApiManager || !this.pvtechApiManager.isInitialized || this.firstUpdate) {
        console.log('PVTechAPIManager not yet initialized, skipping update', this.firstUpdate, this.pvtechApiManager);
        this.firstUpdate = false;
        
        const isMmap = this.data.dataSource === 'mmap';
        if (isMmap && this.currentData) {
            console.log('[StockCubeVisualizer] mmap data exists, continuing update for filtering');
        } else {
            return;
        }
    }

    console.log('PVTechAPIManager is initialized, proceeding with update');

    if (
        JSON.stringify(prevData.sectorIds) !== JSON.stringify(this.data.sectorIds) ||
        prevData.dataSource !== this.data.dataSource ||
        prevData.selector !== this.data.selector ||
        prevData.topN !== this.data.topN ||
        prevData.interval !== this.data.interval ||
        prevData.injectedData !== this.data.injectedData ||
        prevData.watchlistId !== this.data.watchlistId
    ) {
        console.log('Data changed, fetching new data');
        this.updateSelectorVisibility();
        
        const isMmap = this.data.dataSource === 'mmap';
        const onlySectorsOrDepthChanged = prevData.dataSource === this.data.dataSource && 
                                         prevData.interval === this.data.interval &&
                                         (JSON.stringify(prevData.sectorIds) !== JSON.stringify(this.data.sectorIds) || 
                                          prevData.topN !== this.data.topN);

        if (isMmap && onlySectorsOrDepthChanged && this.currentData) {
            console.log('[StockCubeVisualizer] Re-rendering Market Map with new sector settings');
            if (window.MarketMapSharedRendering && typeof window.MarketMapSharedRendering.renderMarketMap === 'function') {
                window.MarketMapSharedRendering.renderMarketMap(this, this.currentData);
            }
        } else {
            this.isFirstRender = true;
            this.maxHeightValue = 0;
            this.hasNegativeGlobal = false;
            this.isFirstAnimationPlay = true;
            this.hasShownZeroFrame = false;

            if (window.StockCubeCore && window.StockCubeCore.cancelTimelineAnimation) {
                window.StockCubeCore.cancelTimelineAnimation(this.data.timelineEntity);
            }

            this.fireworksController?.clearFireworkIntervals();
            if (window.Fireworks) window.Fireworks.emergencyStopFireworks();

            this.fetchStockData();
        }
    }

    const FIREWORKS_EXCLUDED_SOURCES = ['mmap', 'sip', 'sip-sec', 'dividend', 'dividend-sec',
        'top-dollar-vol-sec', 'top-gainers-sec', 'top-losers-sec', 'top-vol-sec'];
    if (FIREWORKS_EXCLUDED_SOURCES.includes(this.data.dataSource) && window.Fireworks) {
        window.Fireworks.emergencyStopFireworks();
        this.fireworksController?.clearFireworkIntervals();
    }
};

window.StockCubeCore.remove = function () {
    if (this.onInjectedData) {
        this.el.removeEventListener('watchlist-injected-data', this.onInjectedData);
    }
    if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
    }
    if (this._iconRefreshTimeout) {
        clearTimeout(this._iconRefreshTimeout);
        this._iconRefreshTimeout = null;
    }
    this.fireworksController?.destroy();
    this.updateSelectorVisibility(false);
    this.currentFetchId++;
    if (this.errorDialog && this.el.sceneEl) {
        this.el.sceneEl.removeChild(this.errorDialog);
    }
    if (this.configureChangedListener) {
        document.removeEventListener('configure-changed', this.configureChangedListener);
        this.el.removeEventListener('configure-changed', this.configureChangedListener);
    }
    if (this.rotationUpdateListener) {
        this.scene.removeEventListener('update-rotation', this.rotationUpdateListener);
    }
    if (this.sipFiltersChangedListener) {
        this.scene.removeEventListener('sip-filters-changed', this.sipFiltersChangedListener);
    }
    if (this.chartDataFetchStartedListener) {
        this.scene.removeEventListener('chart-data-fetch-started', this.chartDataFetchStartedListener);
    }
    if (this.chartDataFetchCompleteListener) {
        this.scene.removeEventListener('chart-data-fetch-complete', this.chartDataFetchCompleteListener);
    }
    if (this.chartDataFetchErrorListener) {
        this.scene.removeEventListener('chart-data-fetch-error', this.chartDataFetchErrorListener);
    }
    if (this.emissivePulse && this.emissivePulse.bars) {
        this.emissivePulse.bars.clear();
    }
    this.removeCompanyIconsFromAllBars?.();
    if (this._onThemeChanged) {
        this.el.sceneEl.removeEventListener('theme-changed', this._onThemeChanged);
    }
    if (this._themeObserver) {
        this._themeObserver.disconnect();
        this._themeObserver = null;
    }
};
